// Grafik-controller: vælg en grafik, ret teksterne, afprøv den i preview og
// send den på (program = det vMix-outputtet /gfx viser lige nu).

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { GfxCommand, GfxGraphicId, GfxPage, GfxPreset, GfxTimerId } from "@mma/shared";
import { api, errorMessage } from "../api.js";
import { GRAPHICS, resolve, type GfxCtx, type GraphicDef } from "../gfx/graphics.js";
import { clock, roundOf, timerRemaining, timerRunning, timerState } from "../gfx/ctx.js";
import { GfxScreen } from "../gfx/Screen.js";
import { Stage } from "../gfx/Stage.js";
import { StagePlayer } from "../gfx/player.js";
import { useGfxStream, useServerNow } from "../gfx/useGfx.js";
import { EVENT_LABELS, SpotterPresence, finalFight, fmtTime } from "../gfx/spotter/SpotterGraphics.js";

interface FightOption {
  id: string;
  name: string;
  fights: { id: string; status: string; blue: string; red: string }[];
}

const CHECKER = { background: "repeating-conic-gradient(#262626 0% 25%, #1c1c1c 0% 50%) 50% / 24px 24px" };
const BTN = "px-3 py-1.5 rounded bg-neutral-700 hover:bg-neutral-600 text-sm disabled:opacity-40";

async function send(cmd: GfxCommand) {
  try {
    await api.post("/api/gfx/command", cmd);
  } catch (err) {
    alert(errorMessage(err, "Kommandoen gik ikke igennem"));
  }
}

// Klokkeslæt i dansk tid, fx "18:20".
const hhmm = (ms: number) =>
  new Intl.DateTimeFormat("da-DK", { timeZone: "Europe/Copenhagen", hour: "2-digit", minute: "2-digit" })
    .format(new Date(ms))
    .replace(".", ":");

function useFightOptions() {
  const [events, setEvents] = useState<FightOption[]>([]);
  useEffect(() => {
    api.get<FightOption[]>("/api/gfx/fights").then(setEvents);
  }, []);
  return events;
}

function FightSelect({
  value,
  onChange,
  events,
  autoLabel,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  events: FightOption[];
  autoLabel: string;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="bg-neutral-800 rounded px-2 py-1.5 max-w-full text-sm"
    >
      <option value="">{autoLabel}</option>
      {events.map((ev) => (
        <optgroup key={ev.id} label={ev.name}>
          {ev.fights.map((x, i) => (
            <option key={x.id} value={x.id}>
              #{i + 1} {x.blue} vs {x.red}
              {x.status === "live" ? " · live" : x.status === "finished" ? " · færdig" : ""}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

// 1920×1080 skaleret ned til bredden af boksen.
function Monitor({ label, tone, children }: { label: ReactNode; tone: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(e!.contentRect.width / 1920));
    ro.observe(ref.current!);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="space-y-1 min-w-0">
      <div className={`text-xs font-semibold tracking-widest ${tone}`}>{label}</div>
      <div ref={ref} className="relative w-full aspect-video overflow-hidden rounded-lg border border-neutral-800" style={CHECKER}>
        <div style={{ position: "absolute", width: 1920, height: 1080, transform: `scale(${scale})`, transformOrigin: "0 0" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-neutral-950/60 border border-neutral-800 rounded-lg p-3 space-y-2">
      <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{title}</div>
      {children}
    </div>
  );
}

function TimerButtons({ id, ctx, resetSec }: { id: GfxTimerId; ctx: GfxCtx; resetSec: number }) {
  const t = timerState(ctx, id);
  const running = timerRunning(t);
  return (
    <>
      <span className="font-mono text-2xl tabular-nums w-20">{clock(timerRemaining(t, ctx.now))}</span>
      <button onClick={() => send({ type: "timer", timer: id, action: running ? "pause" : "start" })} className={BTN}>
        {running ? "Pause" : "Start"}
      </button>
      <button onClick={() => send({ type: "timer", timer: id, action: "reset", durationSec: resetSec })} className={BTN}>
        Nulstil ({clock(resetSec)})
      </button>
    </>
  );
}

// Rundeur, rundemarkør og medical break i UR.
function UrControls({ ctx }: { ctx: GfxCtx }) {
  const fight = ctx.data.fight;
  const roundSec = fight?.roundDurationSec ?? 300;
  const { current, total } = roundOf(ctx);
  const setRound = (c: number, t = total) => send({ type: "round", fightId: fight?.id ?? null, current: c, total: t });
  const nextRound = async () => {
    await setRound(Math.min(total, current + 1));
    await send({ type: "timer", timer: "round", action: "reset", durationSec: roundSec });
  };
  const medical = ctx.state.medical;
  return (
    <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-3">
      <Panel title="Rundeur">
        <div className="flex flex-wrap items-center gap-2">
          <TimerButtons id="round" ctx={ctx} resetSec={roundSec} />
        </div>
      </Panel>
      <Panel title="Runde">
        <div className="flex flex-wrap items-center gap-1.5">
          {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => setRound(n)}
              className={`w-10 py-1.5 rounded text-sm ${n === current ? "bg-white text-black font-bold" : "bg-neutral-700 hover:bg-neutral-600"}`}
            >
              R{n}
            </button>
          ))}
          <button onClick={nextRound} disabled={current >= total} className={`${BTN} ml-1`} title="Næste runde og nulstil uret">
            Næste runde
          </button>
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-400">
          Antal runder
          <select
            value={total}
            onChange={(e) => setRound(Math.min(current, Number(e.target.value)), Number(e.target.value))}
            className="bg-neutral-800 rounded px-2 py-1 text-sm text-neutral-100"
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </Panel>
      <Panel title="Strikes over uret">
        <button
          onClick={() => send({ type: "strikes", on: !ctx.state.strikes })}
          className={`w-full py-2.5 rounded-lg font-semibold ${
            ctx.state.strikes ? "bg-sky-500 text-black hover:bg-sky-400" : "bg-neutral-700 hover:bg-neutral-600"
          }`}
        >
          {ctx.state.strikes ? "Strikes vises — tilbage til uret" : "Vis strikes"}
        </button>
        <p className="text-xs text-neutral-500">
          {fight
            ? `${fight.stats.blue.total_strikes_landed} – ${fight.stats.red.total_strikes_landed} landede. Uret kører videre imens.`
            : "Ingen kamp."}
        </p>
      </Panel>
      <Panel title="Medical break">
        <button
          onClick={() => send({ type: "medical", on: !medical })}
          className={`w-full py-2.5 rounded-lg font-semibold ${
            medical ? "bg-amber-500 text-black hover:bg-amber-400 animate-pulse" : "bg-neutral-700 hover:bg-neutral-600"
          }`}
        >
          {medical
            ? `Medical break ${clock(timerRemaining(timerState(ctx, "medical"), ctx.now))} — afslut`
            : "Start medical break"}
        </button>
        <p className="text-xs text-neutral-500">Pauser rundeuret og viser 5:00 nedtælling i stedet for uret.</p>
      </Panel>
    </div>
  );
}

function CountdownControls({ ctx }: { ctx: GfxCtx }) {
  const t = timerState(ctx, "countdown");
  const startTime = ctx.data.next?.startTime?.replace(".", ":") ?? "";
  const [at, setAt] = useState(startTime);
  const [minutes, setMinutes] = useState(String(t.durationSec / 60));
  useEffect(() => setAt(startTime), [startTime]);
  const minutesSec = Math.round(Number(minutes.replace(",", ".")) * 60) || t.durationSec;
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Panel title={t.targetAt != null ? `Tæller ned til kl. ${hhmm(t.targetAt)}` : "Ur"}>
        <div className="flex flex-wrap items-center gap-2">
          <TimerButtons id="countdown" ctx={ctx} resetSec={minutesSec} />
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-400">
          Længde
          <input
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            className="w-16 bg-neutral-800 rounded px-2 py-1 text-sm text-neutral-100"
            inputMode="decimal"
          />
          min (bruges af Nulstil)
        </label>
      </Panel>
      <Panel title="Tæl ned til et klokkeslæt">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={at}
            onChange={(e) => setAt(e.target.value)}
            placeholder="18:20"
            className="w-20 bg-neutral-800 rounded px-2 py-1.5 text-sm"
          />
          <button
            onClick={() => send({ type: "timer", timer: "countdown", action: "target", at })}
            disabled={!at.trim()}
            className={BTN}
          >
            Tæl ned til kl. {at || "…"}
          </button>
        </div>
        <p className="text-xs text-neutral-500">
          {startTime ? `Udfyldt med næste kamps starttid (${startTime}).` : "Næste kamp har ingen starttid i programmet."}
        </p>
      </Panel>
    </div>
  );
}

function NextMatchControls({ ctx, events }: { ctx: GfxCtx; events: FightOption[] }) {
  const n = ctx.data.next;
  return (
    <Panel title="Næste kamp">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{n ? `${n.blue.name} vs ${n.red.name}` : "Ingen planlagt kamp fundet"}</span>
        <FightSelect
          value={ctx.state.nextFightId}
          onChange={(fightId) => send({ type: "nextFight", fightId })}
          events={events}
          autoLabel="Næste i programmet"
        />
      </div>
    </Panel>
  );
}

function FlashInfo({ ctx }: { ctx: GfxCtx }) {
  const e = ctx.data.fight?.lastEvent;
  return (
    <Panel title="Seneste knockdown / submission">
      <p className="text-sm">
        {e
          ? `${EVENT_LABELS[e.kind]} · ${ctx.data.fight![e.corner].name} · runde ${e.round}, ${fmtTime(e.timeSec)}`
          : "Ingen registreret i kampen endnu — skriv teksten selv."}
      </p>
      <p className="text-xs text-neutral-500">Felterne udfyldes med den seneste. Tryk Ind for at vise den.</p>
    </Panel>
  );
}

function FinalInfo({ ctx }: { ctx: GfxCtx }) {
  const f = finalFight(ctx);
  return (
    <Panel title="Viser">
      <p className="text-sm">
        {f ? `${f.blue.name} vs ${f.red.name}` : "Ingen afsluttet kamp endnu"}
        {f && !f.resultMethod && " — intet resultat sat"}
      </p>
      <p className="text-xs text-neutral-500">Den valgte kamp hvis den er afsluttet, ellers den senest afsluttede.</p>
    </Panel>
  );
}

// Gemte navne til Navn + titel og Lille navn.
function Presets({ def, manual, presets }: { def: GraphicDef; manual: Record<string, string>; presets: GfxPreset[] }) {
  const withTitle = def.presets === "name+title";
  const name = manual.name ?? "";
  const title = withTitle ? (manual.title ?? "") : "";
  const apply = (p: { name: string; title: string }) =>
    send({ type: "fields", graphic: def.id, fields: withTitle ? { name: p.name, title: p.title } : { name: p.name } });
  return (
    <Panel title="Gemte navne">
      <div className="flex flex-wrap gap-1.5">
        {presets.length === 0 && <span className="text-xs text-neutral-500">Ingen endnu — skriv et navn og gem det.</span>}
        {presets.map((p) => (
          <span key={p.id} className="inline-flex items-center rounded bg-neutral-800 text-sm">
            <button onClick={() => apply(p)} className="px-2.5 py-1 hover:bg-neutral-700 rounded-l">
              {p.name}
              {withTitle && p.title && <span className="text-neutral-400"> · {p.title}</span>}
            </button>
            <button
              onClick={() => confirm(`Slet "${p.name}"?`) && send({ type: "preset", action: "delete", id: p.id })}
              className="px-1.5 py-1 text-neutral-500 hover:text-red-400"
              title="Slet"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <button
        onClick={() => send({ type: "preset", action: "save", name, title })}
        disabled={!name.trim()}
        className={BTN}
      >
        Gem "{name || "…"}"{withTitle && title ? ` · ${title}` : ""}
      </button>
    </Panel>
  );
}

// Tekstfelter. Tomt felt = værdien fra kampen (vist som pladsholder).
function Fields({ def, manual, ctx }: { def: GraphicDef; manual: Record<string, string>; ctx: GfxCtx }) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => setDrafts({}), [def.id]);

  function edit(key: string, value: string) {
    setDrafts((d) => ({ ...d, [key]: value }));
    clearTimeout(timers.current.get(key));
    timers.current.set(
      key,
      setTimeout(async () => {
        await send({ type: "fields", graphic: def.id, fields: { [key]: value } });
        setDrafts(({ [key]: _, ...rest }) => rest);
      }, 350),
    );
  }

  const { fields } = resolve(def, manual, ctx);
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-2">
      {fields.map(({ def: f, auto, manual: isManual }) => (
        <div key={f.key} className="contents">
          <label className="text-sm text-neutral-400">{f.label}</label>
          <input
            value={drafts[f.key] ?? manual[f.key] ?? ""}
            placeholder={auto || "—"}
            onChange={(e) => edit(f.key, e.target.value)}
            className={`bg-neutral-800 rounded px-3 py-1.5 ${isManual ? "ring-1 ring-amber-600" : ""}`}
          />
          {isManual && (f.auto || f.fallback) ? (
            <button
              onClick={() => edit(f.key, "")}
              title="Brug værdien fra kampen igen"
              className="text-xs text-amber-400 hover:text-amber-300"
            >
              Auto
            </button>
          ) : (
            <span />
          )}
        </div>
      ))}
    </div>
  );
}

export function AdminGfxPage() {
  const { snapshot, connected, offset } = useGfxStream();
  const now = useServerNow(offset);
  const events = useFightOptions();
  const [selected, setSelected] = useState<GfxGraphicId>("ur");
  const def = GRAPHICS.find((g) => g.id === selected)!;
  // Preview: vMix-titler afspilles af en StagePlayer, spotter-grafikker
  // monteres/fades som på outputtet.
  const preview = useMemo(() => (def.spec ? new StagePlayer(def.spec) : null), [def]);
  const [pv, setPv] = useState({ onAir: true, cue: 1 });
  const previewIn = () => (preview ? preview.play("in") : setPv((p) => ({ onAir: true, cue: p.cue + 1 })));
  const previewOut = () => (preview ? preview.playOut() : setPv((p) => ({ ...p, onAir: false })));

  useEffect(() => {
    preview?.show(["in"]);
    setPv((p) => ({ onAir: true, cue: p.cue + 1 }));
  }, [preview, def]);

  if (!snapshot) return <div className="p-6 text-neutral-400">Forbinder…</div>;

  const ctx: GfxCtx = { data: snapshot.data, state: snapshot.state, now };
  const g = snapshot.state.graphics[selected];
  const manual = g?.fields ?? {};
  const onAir = !!g?.onAir;
  const previewTexts = resolve(def, manual, ctx);
  const outputUrl = `${location.origin}/gfx`;
  const f = snapshot.data.fight;

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">Grafik</h1>
        <span className={`text-xs ${connected ? "text-emerald-400" : "text-red-400"}`}>
          ● {connected ? "Forbundet" : "Ingen forbindelse — prøver igen"}
        </span>
        <button onClick={() => send({ type: "allOut" })} className="ml-auto px-3 py-1.5 rounded bg-red-900 hover:bg-red-800 text-sm">
          Alt ud
        </button>
        <Link to="/admin" className="text-sm text-neutral-400 hover:text-neutral-200">
          ← Events
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <FightSelect
          value={snapshot.state.fightId}
          onChange={(fightId) => send({ type: "fight", fightId })}
          events={events}
          autoLabel="Følg kampen der er live"
        />
        <span className="text-neutral-400">
          {f ? `${f.blue.name} vs ${f.red.name}` : "Ingen kamp — navne skal skrives manuelt"}
        </span>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        <nav className="flex lg:flex-col flex-wrap gap-1">
          {GRAPHICS.map((x, i) => {
            const live = snapshot.state.graphics[x.id]?.onAir;
            const header = x.group && x.group !== GRAPHICS.slice(0, i).filter((y) => y.group).at(-1)?.group;
            return (
              <Fragment key={x.id}>
              {header && (
                <div className="w-full px-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                  {x.group}
                </div>
              )}
              <button
                onClick={() => setSelected(x.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm ${
                  x.id === selected ? "bg-neutral-700" : "bg-neutral-900 hover:bg-neutral-800"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${live ? "bg-red-500" : "bg-neutral-700"}`} />
                <span className="flex-1">{x.label}</span>
                {live && <span className="text-[10px] font-bold text-red-400">PÅ</span>}
              </button>
              </Fragment>
            );
          })}
        </nav>

        <div className="space-y-4 min-w-0">
          <div className="grid md:grid-cols-2 gap-3">
            <Monitor label="PREVIEW" tone="text-emerald-400">
              {def.Html ? (
                <SpotterPresence id={def.id} onAir={pv.onAir} cue={pv.cue} instant={false}>
                  <def.Html ctx={ctx} texts={previewTexts.texts} />
                </SpotterPresence>
              ) : (
                <Stage
                  spec={def.spec!}
                  player={preview!}
                  texts={previewTexts.texts}
                  visible={previewTexts.visible}
                  images={previewTexts.images}
                >
                  {def.Extra && <def.Extra ctx={ctx} />}
                </Stage>
              )}
            </Monitor>
            <Monitor label="PROGRAM" tone="text-red-400">
              <GfxScreen snapshot={snapshot} now={now} />
            </Monitor>
          </div>

          <section className="bg-neutral-900 rounded-xl p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold mr-2">{def.label}</h2>
              <button
                onClick={() => send({ type: "in", graphic: selected })}
                className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 font-semibold"
              >
                {onAir ? "Ind igen" : "Ind"}
              </button>
              {def.pages &&
                (["page1", "page2"] as GfxPage[]).map((p) => (
                  <button
                    key={p}
                    disabled={!onAir}
                    onClick={() => send({ type: "page", graphic: selected, page: p })}
                    className="px-4 py-2.5 rounded-lg bg-neutral-700 hover:bg-neutral-600 disabled:opacity-40"
                  >
                    {def.pages![p]}
                  </button>
                ))}
              <button
                disabled={!onAir}
                onClick={() => send({ type: "out", graphic: selected })}
                className="px-5 py-2.5 rounded-lg bg-neutral-700 hover:bg-neutral-600 disabled:opacity-40 font-semibold"
              >
                Ud
              </button>

              <span className="ml-auto text-xs text-neutral-500">Afprøv i preview:</span>
              <button onClick={previewIn} className="px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-xs">
                Ind
              </button>
              {def.pages &&
                (["page1", "page2"] as GfxPage[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => preview?.play(p)}
                    className="px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-xs"
                  >
                    {def.pages![p]}
                  </button>
                ))}
              <button onClick={previewOut} className="px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-xs">
                Ud
              </button>
            </div>

            {selected === "ur" && <UrControls ctx={ctx} />}
            {selected === "countdown" && <CountdownControls ctx={ctx} />}
            {selected === "nextMatch" && <NextMatchControls ctx={ctx} events={events} />}
            {selected === "flash" && <FlashInfo ctx={ctx} />}
            {(selected === "recap" || selected === "breakdown") && (
              <Panel title="Kamp">
                <p className="text-sm">{f ? `${f.blue.name} vs ${f.red.name}` : "Ingen kamp valgt eller live"}</p>
              </Panel>
            )}
            {selected === "final" && <FinalInfo ctx={ctx} />}
            {def.presets && <Presets def={def} manual={manual} presets={snapshot.state.presets} />}

            <Fields def={def} manual={manual} ctx={ctx} />
          </section>

          <section className="text-xs text-neutral-500 space-y-1">
            <p>
              vMix: tilføj et Web Browser-input (1920×1080) med <code className="text-neutral-300">{outputUrl}</code> — så
              styres alle grafikker herfra.
            </p>
            <p>
              Kun denne grafik: <code className="text-neutral-300">{`${outputUrl}/${selected}`}</code>. Tilføj{" "}
              <code className="text-neutral-300">?auto</code> hvis inputtet i stedet skal styres med vMix' egne
              overlay-knapper (grafikken spiller ind når siden indlæses).
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
